import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Mail, Send, RefreshCw, Users, FileText, CheckCircle, AlertCircle, 
    Sparkles, Calendar, Eye, Edit3, Trash2, Clock, ArrowRight, 
    MailOpen, UserPlus, Zap, X, ChevronRight, Wand2, Save, Image, 
    ChevronLeft, ChevronDown, Type, Palette, Layout, FlaskConical, 
    CalendarClock, Copy, Play, Pause
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available images for the newsletter
const AVAILABLE_IMAGES = {
    hero: [
        "/assets/pedros/1.jpg",
        "/assets/pedros/2.jpg",
        "/assets/pedros/3.jpg",
        "/assets/pedros/4.jpg",
        "/assets/pedros/5.jpg",
        "/assets/pedros/6.jpg",
        "/assets/pedros/7.jpg",
        "/assets/pedros/8.jpg",
        "/assets/twilight.jpg",
        "/assets/kontakt_vogel_hq.jpg",
    ],
    waldblick: [
        "/assets/waldblick/1.jpg",
        "/assets/waldblick/2.jpg",
        "/assets/waldblick/3.jpg",
        "/assets/waldblick/4.jpg",
        "/assets/waldblick/5.jpg",
    ],
    seeblick: [
        "/assets/seeblick/1.jpg",
        "/assets/seeblick/2.jpg",
        "/assets/seeblick/3.jpg",
        "/assets/seeblick/4.jpg",
        "/assets/seeblick/5.jpg",
        "/assets/seeblick/6.jpg",
        "/assets/seeblick/7.jpg",
        "/assets/seeblick/8.jpg",
    ],
    closing: [
        "/assets/pedros/9.jpg",
        "/assets/pedros/10.jpg",
        "/assets/pedros/11.jpg",
        "/assets/pedros/12.jpg",
        "/assets/twilight.jpg",
        "/assets/event_building.png",
    ]
};

export const NewsletterAdmin = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [drafts, setDrafts] = useState([]);
    const [stats, setStats] = useState({ total_subscribers: 0, total_newsletters_sent: 0 });
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);
    const [refining, setRefining] = useState(false);
    const [showEditor, setShowEditor] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    
    // Visual Editor State
    const [newsletterData, setNewsletterData] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [showImagePicker, setShowImagePicker] = useState(null);
    const [refinePrompt, setRefinePrompt] = useState('');
    const [deletingSubscriber, setDeletingSubscriber] = useState(null);
    
    // New Features State
    const [activeTab, setActiveTab] = useState('create'); // create, templates, scheduled, abtest
    const [templates, setTemplates] = useState([]);
    const [scheduled, setScheduled] = useState([]);
    const [abTests, setAbTests] = useState([]);
    const [showTemplateDialog, setShowTemplateDialog] = useState(false);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [showABTestDialog, setShowABTestDialog] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [scheduleDate, setScheduleDate] = useState('');
    const [abTestSubjectA, setAbTestSubjectA] = useState('');
    const [abTestSubjectB, setAbTestSubjectB] = useState('');
    const [abTestPercentage, setAbTestPercentage] = useState(20);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        loadData();
        loadTemplates();
        loadScheduled();
        loadABTests();
    }, []);

    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const loadTemplates = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/newsletter/templates`, { headers });
            setTemplates(res.data);
        } catch (error) {
            console.error('Error loading templates:', error);
        }
    };

    const loadScheduled = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/newsletter/scheduled`, { headers });
            setScheduled(res.data);
        } catch (error) {
            console.error('Error loading scheduled:', error);
        }
    };

    const loadABTests = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/newsletter/ab-tests`, { headers });
            setAbTests(res.data);
        } catch (error) {
            console.error('Error loading A/B tests:', error);
        }
    };

    const saveAsTemplate = async () => {
        if (!templateName.trim() || !newsletterData) return;
        try {
            await axios.post(`${API_URL}/api/newsletter/templates`, null, {
                headers,
                params: {
                    name: templateName,
                    subject: newsletterData.subject,
                    content: buildNewsletterHTML()
                }
            });
            setMessage({ type: 'success', text: 'Vorlage gespeichert!' });
            setShowTemplateDialog(false);
            setTemplateName('');
            loadTemplates();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Speichern der Vorlage' });
        }
    };

    const deleteTemplate = async (id) => {
        if (!window.confirm('Vorlage wirklich löschen?')) return;
        try {
            await axios.delete(`${API_URL}/api/newsletter/templates/${id}`, { headers });
            setMessage({ type: 'success', text: 'Vorlage gelöscht' });
            loadTemplates();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const scheduleNewsletter = async () => {
        if (!scheduleDate || !newsletterData) return;
        try {
            await axios.post(`${API_URL}/api/newsletter/schedule`, null, {
                headers,
                params: {
                    subject: newsletterData.subject,
                    content: buildNewsletterHTML(),
                    scheduled_for: new Date(scheduleDate).toISOString()
                }
            });
            setMessage({ type: 'success', text: 'Newsletter geplant!' });
            setShowScheduleDialog(false);
            setScheduleDate('');
            loadScheduled();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Planen' });
        }
    };

    const cancelScheduled = async (id) => {
        if (!window.confirm('Geplanten Newsletter wirklich abbrechen?')) return;
        try {
            await axios.delete(`${API_URL}/api/newsletter/scheduled/${id}`, { headers });
            setMessage({ type: 'success', text: 'Geplanter Newsletter abgebrochen' });
            loadScheduled();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Abbrechen' });
        }
    };

    const createABTest = async () => {
        if (!abTestSubjectA.trim() || !abTestSubjectB.trim() || !newsletterData) return;
        try {
            await axios.post(`${API_URL}/api/newsletter/ab-test`, null, {
                headers,
                params: {
                    subject_a: abTestSubjectA,
                    subject_b: abTestSubjectB,
                    content: buildNewsletterHTML(),
                    test_percentage: abTestPercentage
                }
            });
            setMessage({ type: 'success', text: 'A/B-Test erstellt!' });
            setShowABTestDialog(false);
            setAbTestSubjectA('');
            setAbTestSubjectB('');
            loadABTests();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Erstellen des A/B-Tests' });
        }
    };

    const sendABTest = async (testId) => {
        if (!window.confirm('A/B-Test jetzt an Testgruppe senden?')) return;
        try {
            const res = await axios.post(`${API_URL}/api/newsletter/ab-test/${testId}/send`, {}, { headers });
            setMessage({ type: 'success', text: `A/B-Test gesendet! Gruppe A: ${res.data.group_a_sent}, Gruppe B: ${res.data.group_b_sent}` });
            loadABTests();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Senden des A/B-Tests' });
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [subsRes, draftsRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/api/newsletter/subscribers`, { headers }),
                axios.get(`${API_URL}/api/newsletter/drafts`, { headers }),
                axios.get(`${API_URL}/api/newsletter/stats`, { headers })
            ]);
            setSubscribers(subsRes.data);
            setDrafts(draftsRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error loading data:', error);
            setMessage({ type: 'error', text: 'Fehler beim Laden der Daten' });
        }
        setLoading(false);
    };

    const deleteDraft = async (draftId) => {
        if (!window.confirm('Möchten Sie diesen Newsletter-Entwurf wirklich löschen?')) return;
        try {
            await axios.delete(`${API_URL}/api/newsletter/drafts/${draftId}`, { headers });
            setMessage({ type: 'success', text: 'Entwurf gelöscht' });
            loadData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const deleteAllDrafts = async () => {
        if (!window.confirm('Möchten Sie ALLE Newsletter-Entwürfe wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
        try {
            await axios.delete(`${API_URL}/api/newsletter/drafts`, { headers });
            setMessage({ type: 'success', text: 'Alle Entwürfe gelöscht' });
            loadData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const deleteAllSubscribers = async () => {
        if (!window.confirm('Möchten Sie ALLE Abonnenten wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) return;
        try {
            await axios.delete(`${API_URL}/api/newsletter/subscribers`, { headers });
            setMessage({ type: 'success', text: 'Alle Abonnenten gelöscht' });
            loadData();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const generateNewsletter = async () => {
        setGenerating(true);
        setMessage({ type: '', text: '' });
        try {
            const response = await axios.post(`${API_URL}/api/newsletter/generate`, {}, { headers });
            
            // Parse the HTML content into editable fields
            const parsed = parseNewsletterHTML(response.data.content);
            setNewsletterData({
                subject: response.data.subject,
                ...parsed,
                rawHtml: response.data.content
            });
            
            setShowEditor(true);
            setMessage({ type: 'success', text: 'Newsletter generiert! Klicke auf Texte und Bilder zum Bearbeiten.' });
            loadData();
        } catch (error) {
            console.error('Error generating:', error);
            setMessage({ type: 'error', text: 'Fehler beim Generieren' });
        }
        setGenerating(false);
    };

    // Parse HTML to extract editable content
    const parseNewsletterHTML = (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract key elements - look for common patterns
        const getText = (selector) => {
            const el = doc.querySelector(selector);
            return el ? el.textContent.trim() : '';
        };
        
        const getImgSrc = (selector) => {
            const el = doc.querySelector(selector);
            return el ? el.getAttribute('src') : '';
        };

        // Try to find images
        const images = doc.querySelectorAll('img');
        let heroImage = '/assets/pedros/1.jpg';
        let roomImage = '/assets/waldblick/1.jpg';
        let closingImage = '/assets/pedros/9.jpg';
        
        images.forEach((img, idx) => {
            const src = img.getAttribute('src') || '';
            if (idx === 0) heroImage = src;
            else if (src.includes('waldblick') || src.includes('seeblick') || idx === 1) roomImage = src;
            else if (idx >= 2) closingImage = src;
        });

        // Extract text content
        const h1 = doc.querySelector('h1');
        const headline = h1 ? h1.textContent.trim() : 'Newsletter Headline';
        
        // Find paragraphs
        const paragraphs = doc.querySelectorAll('p');
        let subline = '';
        let intro = '';
        paragraphs.forEach((p, idx) => {
            const text = p.textContent.trim();
            if (idx === 0 && text.length < 100) subline = text;
            else if (idx <= 2 && text.length > 50) intro = text;
        });

        // Find room info
        const roomNameEl = doc.querySelector('[style*="font-size: 20px"]') || doc.querySelector('[style*="font-weight: 700"]');
        const roomName = roomNameEl ? roomNameEl.textContent.trim().split('–')[0].trim() : 'Waldblick';
        
        // Get discount info
        const discountMatch = html.match(/(\d+)%\s*sparen/);
        const discount = discountMatch ? discountMatch[1] : '15';
        
        // Get tip
        const tipSection = html.match(/Tipp des Monats<\/div>\s*<div[^>]*>([^<]+)/);
        const tip = tipSection ? tipSection[1].trim() : 'Produktivitäts-Tipp: Die beste Kreativität entsteht in inspirierender Umgebung.';
        
        // Get closing
        const closingMatch = html.match(/font-size: 17px[^>]*>([^<]+)/);
        const closing = closingMatch ? closingMatch[1].trim() : 'Wir freuen uns auf dich!';

        return {
            heroImage,
            roomImage,
            closingImage,
            headline,
            subline: subline || 'Dein Update aus dem teamWERK',
            intro: intro || 'Ein neuer Monat bringt frische Energie ins teamWERK.',
            roomName,
            roomDescription: 'Moderner Besprechungsraum für kreative Sessions',
            discount,
            discountReason: 'Winter-Special',
            tip,
            closing,
            featuredRoom: roomImage.includes('seeblick') ? 'seeblick' : 'waldblick'
        };
    };

    // Build final HTML from visual data
    const buildNewsletterHTML = () => {
        if (!newsletterData) return '';
        
        const now = new Date();
        const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
                       'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
        const month = months[now.getMonth()];
        const year = now.getFullYear();
        
        const roomData = newsletterData.featuredRoom === 'seeblick' ? {
            name: 'Seeblick',
            tagline: 'Großer Auftritt, große Wirkung',
            desc: 'Großer Tagungsraum für Konferenzen, Seminare und Schulungen',
            size: '90m² + Vorstadt 60m²',
            capacity: 'Bis 50 Personen',
            features: '98" 4K Screen · Samsung Flip 55" · Magnetwand · WLAN',
            url: 'https://anny.co/book/besprechungsraum-seeblick-fzugqlwtkpc8k5vx'
        } : {
            name: 'Waldblick',
            tagline: 'Kreative Sessions mit Stil',
            desc: 'Moderner Besprechungsraum für kleine Meetings und Workshops',
            size: '27m²',
            capacity: 'Bis 8 Personen',
            features: '65" Touchscreen · Kaffee inklusive · Dimmbares Licht · WLAN',
            url: 'https://anny.co/book/besprechungsraum-waldblick-vxoketwugabmmmvy'
        };

        return `<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>teamWERK Newsletter - ${month} ${year}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background-color: #f5f5f5;">
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #f5f5f5;">
        <tr>
            <td align="center" style="padding: 20px;">
                <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background: #fff;">
                    <!-- Header -->
                    <tr>
                        <td style="padding: 25px 40px; border-bottom: 1px solid #eee;">
                            <table cellpadding="0" cellspacing="0" border="0" width="100%">
                                <tr>
                                    <td>
                                        <span style="font-size: 22px; font-weight: 800; letter-spacing: -1px; color: #000;">TEAM</span><span style="font-size: 22px; font-weight: 800; letter-spacing: -1px; color: #666;">WERK</span>
                                    </td>
                                    <td align="right" style="font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 1px;">
                                        📧 ${month} ${year}
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    
                    <!-- Hero Image -->
                    <tr>
                        <td>
                            <img src="${newsletterData.heroImage}" alt="teamWERK" style="width: 100%; height: 220px; object-fit: cover; display: block;" />
                        </td>
                    </tr>
                    
                    <!-- Hero Text -->
                    <tr>
                        <td style="padding: 35px 40px 25px 40px;">
                            <h1 style="font-size: 28px; font-weight: 800; color: #000; margin: 0 0 8px 0; line-height: 1.15; letter-spacing: -0.5px;">
                                ${newsletterData.headline}
                            </h1>
                            <p style="font-size: 15px; color: #666; margin: 0 0 20px 0; line-height: 1.4;">
                                ${newsletterData.subline}
                            </p>
                            <p style="font-size: 15px; color: #333; line-height: 1.65; margin: 0;">
                                ${newsletterData.intro}
                            </p>
                        </td>
                    </tr>
                    
                    <!-- Featured Room -->
                    <tr>
                        <td style="padding: 0 40px 35px 40px;">
                            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #000; color: #fff;">
                                <tr>
                                    <td>
                                        <img src="${newsletterData.roomImage}" alt="${roomData.name}" style="width: 100%; height: 180px; object-fit: cover; display: block;" />
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 22px;">
                                        <div style="display: inline-block; background: #1e3a5f; color: #fff; padding: 5px 12px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px;">
                                            ${newsletterData.discountReason} · ${newsletterData.discount}% sparen
                                        </div>
                                        <div style="font-size: 20px; font-weight: 700; margin-bottom: 6px;">
                                            ${roomData.name} – ${roomData.tagline}
                                        </div>
                                        <div style="font-size: 13px; color: rgba(255,255,255,0.85); margin-bottom: 8px; line-height: 1.5;">
                                            ${roomData.desc}
                                        </div>
                                        <div style="font-size: 12px; color: rgba(255,255,255,0.6); margin-bottom: 4px;">
                                            📐 ${roomData.size} · ${roomData.capacity}
                                        </div>
                                        <div style="font-size: 12px; color: rgba(255,255,255,0.6); margin-bottom: 16px;">
                                            ✓ ${roomData.features}
                                        </div>
                                        <a href="${roomData.url}" style="display: inline-block; background: #fff; color: #000; padding: 12px 24px; text-decoration: none; font-weight: 700; font-size: 13px;">
                                            Jetzt buchen →
                                        </a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    
                    <!-- Tip -->
                    <tr>
                        <td style="padding: 0 40px 35px 40px;">
                            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="border-left: 3px solid #1e3a5f; padding-left: 20px;">
                                <tr>
                                    <td>
                                        <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #999; margin-bottom: 8px;">💡 Tipp des Monats</div>
                                        <div style="font-size: 14px; color: #333; line-height: 1.6;">${newsletterData.tip}</div>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    
                    <!-- Award Badge -->
                    <tr>
                        <td style="padding: 0 40px 30px 40px;">
                            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%); border-radius: 12px;">
                                <tr>
                                    <td style="padding: 20px; text-align: center;">
                                        <img src="/assets/awards/coworking-preis-2025.png" alt="Deutscher Coworking Preis 2025" style="height: 80px; margin-bottom: 12px;" />
                                        <div style="font-size: 14px; font-weight: 700; color: #000; margin-bottom: 6px;">
                                            🏆 1. Platz Deutscher Coworking Preis 2025
                                        </div>
                                        <div style="font-size: 12px; color: #333;">
                                            Kategorie: Coworking Space im ländlichen/suburbanen Raum
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    
                    <!-- Social Media -->
                    <tr>
                        <td style="padding: 0 40px 30px 40px;">
                            <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 2px; color: #999; margin-bottom: 15px;">📱 Folge uns</div>
                            <table cellpadding="0" cellspacing="0" border="0" width="100%">
                                <tr>
                                    <td style="padding: 15px; background: linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%); border-radius: 8px; margin-bottom: 10px;">
                                        <a href="https://www.instagram.com/teamwerk_official/" style="color: #fff; text-decoration: none; display: block;">
                                            <div style="font-weight: 700; font-size: 14px; margin-bottom: 4px;">📸 Instagram</div>
                                            <div style="font-size: 12px; opacity: 0.9;">@teamwerk_official</div>
                                        </a>
                                    </td>
                                </tr>
                                <tr><td style="height: 10px;"></td></tr>
                                <tr>
                                    <td style="padding: 15px; background: #0077b5; border-radius: 8px;">
                                        <a href="https://www.linkedin.com/company/teamwerk-erolzheim/" style="color: #fff; text-decoration: none; display: block;">
                                            <div style="font-weight: 700; font-size: 14px; margin-bottom: 4px;">💼 LinkedIn</div>
                                            <div style="font-size: 12px; opacity: 0.9;">teamwerk-erolzheim</div>
                                        </a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    
                    <!-- Closing -->
                    <tr>
                        <td>
                            <img src="${newsletterData.closingImage}" alt="teamWERK" style="width: 100%; height: 150px; object-fit: cover; display: block;" />
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 30px 40px; text-align: center; background: #000; color: #fff;">
                            <div style="font-size: 17px; font-weight: 600; margin-bottom: 14px;">${newsletterData.closing}</div>
                            <div>
                                <a href="https://www.instagram.com/teamwerk_official/" style="color: rgba(255,255,255,0.7); text-decoration: none; font-size: 12px; margin: 0 10px;">Instagram</a>
                                <a href="https://www.linkedin.com/company/teamwerk-erolzheim/" style="color: rgba(255,255,255,0.7); text-decoration: none; font-size: 12px; margin: 0 10px;">LinkedIn</a>
                                <a href="https://www.teamwerk-erolzheim.de" style="color: rgba(255,255,255,0.7); text-decoration: none; font-size: 12px; margin: 0 10px;">Website</a>
                            </div>
                        </td>
                    </tr>
                    
                    <!-- Footer with Unsubscribe -->
                    <tr>
                        <td style="padding: 20px 40px; background: #fafafa; font-size: 11px; color: #999; text-align: center;">
                            <p style="margin: 0 0 10px 0;">teamWERK Erolzheim · Espachstraße 13 · 88453 Erolzheim · © ${year}</p>
                            <p style="margin: 0;"><a href="{{unsubscribe_link}}" style="color: #999;">Newsletter abmelden</a></p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
    };

    const refineWithAI = async () => {
        if (!refinePrompt.trim() || !newsletterData) {
            setMessage({ type: 'error', text: 'Bitte gib eine Anweisung ein' });
            return;
        }
        
        setRefining(true);
        try {
            const response = await axios.post(
                `${API_URL}/api/newsletter/refine`,
                null,
                { 
                    headers, 
                    params: { 
                        content: buildNewsletterHTML(),
                        prompt: refinePrompt 
                    } 
                }
            );
            
            // Try to apply AI suggestions to specific fields
            const suggestion = response.data.refined_text || '';
            setMessage({ type: 'success', text: 'KI-Vorschlag: ' + suggestion.substring(0, 200) + (suggestion.length > 200 ? '...' : '') });
            setRefinePrompt('');
        } catch (error) {
            console.error('Error refining:', error);
            const errorMsg = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
            setMessage({ type: 'error', text: `Fehler bei der KI-Verfeinerung: ${errorMsg}` });
        }
        setRefining(false);
    };

    const sendNewsletter = async () => {
        if (!newsletterData) {
            setMessage({ type: 'error', text: 'Kein Newsletter zum Senden' });
            return;
        }
        
        if (!window.confirm(`Newsletter an ${stats.total_subscribers} Abonnenten senden?`)) {
            return;
        }

        setSending(true);
        try {
            const finalHTML = buildNewsletterHTML();
            const response = await axios.post(
                `${API_URL}/api/newsletter/send`,
                null,
                { headers, params: { subject: newsletterData.subject, content: finalHTML } }
            );
            setMessage({ type: 'success', text: `Newsletter an ${response.data.sent} Abonnenten gesendet!` });
            setShowEditor(false);
            setNewsletterData(null);
            loadData();
        } catch (error) {
            console.error('Error sending:', error);
            setMessage({ type: 'error', text: 'Fehler beim Versenden' });
        }
        setSending(false);
    };

    const loadDraft = (draft) => {
        const parsed = parseNewsletterHTML(draft.content);
        setNewsletterData({
            subject: draft.subject,
            ...parsed,
            rawHtml: draft.content
        });
        setShowEditor(true);
    };

    const deleteSubscriber = async (email) => {
        if (!window.confirm(`Abonnent "${email}" wirklich löschen?`)) return;
        
        setDeletingSubscriber(email);
        try {
            await axios.delete(`${API_URL}/api/newsletter/subscribers/${encodeURIComponent(email)}`, { headers });
            setMessage({ type: 'success', text: 'Abonnent gelöscht' });
            loadData();
        } catch (error) {
            console.error('Error deleting subscriber:', error);
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
        setDeletingSubscriber(null);
    };

    // Update a field in newsletterData
    const updateField = (field, value) => {
        setNewsletterData(prev => ({ ...prev, [field]: value }));
    };

    // Editable Text Component
    const EditableText = ({ field, value, className, multiline = false, placeholder = "Text eingeben..." }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [tempValue, setTempValue] = useState(value);
        const inputRef = useRef(null);

        useEffect(() => {
            setTempValue(value);
        }, [value]);

        useEffect(() => {
            if (isEditing && inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, [isEditing]);

        const handleSave = () => {
            updateField(field, tempValue);
            setIsEditing(false);
        };

        if (isEditing) {
            return multiline ? (
                <textarea
                    ref={inputRef}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            setTempValue(value);
                            setIsEditing(false);
                        }
                    }}
                    className={`${className} bg-yellow-50 border-2 border-yellow-400 rounded px-2 py-1 w-full resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                    rows={3}
                    placeholder={placeholder}
                />
            ) : (
                <input
                    ref={inputRef}
                    type="text"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSave();
                        if (e.key === 'Escape') {
                            setTempValue(value);
                            setIsEditing(false);
                        }
                    }}
                    className={`${className} bg-yellow-50 border-2 border-yellow-400 rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-yellow-500`}
                    placeholder={placeholder}
                />
            );
        }

        return (
            <div
                onClick={() => setIsEditing(true)}
                className={`${className} cursor-pointer hover:bg-yellow-50 hover:outline hover:outline-2 hover:outline-yellow-400 hover:outline-dashed rounded px-1 transition-all group relative`}
            >
                {value || <span className="text-gray-400 italic">{placeholder}</span>}
                <Edit3 className="w-3 h-3 absolute -right-1 -top-1 text-yellow-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        );
    };

    // Image with Change Button
    const EditableImage = ({ field, src, category, className, alt }) => {
        const images = AVAILABLE_IMAGES[category] || AVAILABLE_IMAGES.hero;
        
        return (
            <div className="relative group">
                <img src={src} alt={alt} className={className} />
                <button
                    onClick={() => setShowImagePicker({ field, category })}
                    className="absolute top-2 right-2 bg-black/80 hover:bg-black text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex items-center gap-2 text-sm font-medium"
                >
                    <Image className="w-4 h-4" />
                    Bild ändern
                </button>
            </div>
        );
    };

    // Image Picker Modal
    const ImagePickerModal = ({ field, category, onClose }) => {
        const images = AVAILABLE_IMAGES[category] || AVAILABLE_IMAGES.hero;
        
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-4 border-b flex items-center justify-between">
                        <h3 className="font-bold text-lg">Bild auswählen</h3>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-4 grid grid-cols-3 gap-3 overflow-y-auto max-h-[60vh]">
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    updateField(field, img);
                                    onClose();
                                }}
                                className={`relative overflow-hidden rounded-lg border-2 hover:border-black transition-all ${
                                    newsletterData?.[field] === img ? 'border-black ring-2 ring-yellow-400' : 'border-gray-200'
                                }`}
                            >
                                <img src={img} alt={`Option ${idx + 1}`} className="w-full h-24 object-cover" />
                                {newsletterData?.[field] === img && (
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                        <CheckCircle className="w-8 h-8 text-white" />
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </motion.div>
            </motion.div>
        );
    };

    // Stats Cards
    const StatsCard = ({ icon: Icon, value, label, color }) => (
        <motion.div 
            whileHover={{ scale: 1.02, y: -2 }}
            className={`relative overflow-hidden rounded-2xl p-6 ${color} text-white shadow-lg`}
        >
            <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                <Icon className="w-full h-full" />
            </div>
            <div className="relative z-10">
                <Icon className="w-8 h-8 mb-3 opacity-80" />
                <p className="text-4xl font-bold mb-1">{value}</p>
                <p className="text-sm opacity-80">{label}</p>
            </div>
        </motion.div>
    );

    // Subscriber Card with Delete
    const SubscriberCard = ({ subscriber }) => (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow group"
        >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {(subscriber.name || subscriber.email)[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">
                    {subscriber.name || 'Kein Name'}
                </p>
                <p className="text-sm text-gray-500 truncate">{subscriber.email}</p>
            </div>
            <button
                onClick={() => deleteSubscriber(subscriber.email)}
                disabled={deletingSubscriber === subscriber.email}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                title="Abonnent löschen"
            >
                {deletingSubscriber === subscriber.email ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                    <Trash2 className="w-4 h-4" />
                )}
            </button>
        </motion.div>
    );

    // Draft Card
    const DraftCard = ({ draft, onLoad, onDelete }) => {
        const isSent = draft.status === 'sent';
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="group relative bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all"
            >
                <div className={`h-2 ${isSent ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-amber-400 to-orange-500'}`} />
                <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            isSent ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                            {isSent ? 'Versendet' : 'Entwurf'}
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 text-gray-400 text-sm">
                                <Calendar className="w-4 h-4" />
                                {new Date(draft.created_at || draft.sent_at).toLocaleDateString('de-DE')}
                            </div>
                            <button
                                onClick={() => onDelete(draft.id)}
                                className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                title="Löschen"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{draft.subject}</h3>
                    {!isSent && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => onLoad(draft)}
                            className="w-full mt-2 py-2.5 px-4 bg-black text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-gray-800"
                        >
                            <Edit3 className="w-4 h-4" />
                            Bearbeiten
                        </motion.button>
                    )}
                </div>
            </motion.div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Toast */}
            <AnimatePresence>
                {message.text && (
                    <motion.div
                        initial={{ opacity: 0, y: -50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-xl shadow-lg flex items-center gap-3 max-w-md ${
                            message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                        }`}
                    >
                        {message.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
                        <span className="font-medium text-sm">{message.text}</span>
                        <button onClick={() => setMessage({ type: '', text: '' })}><X className="w-4 h-4" /></button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Newsletter</h1>
                            <p className="text-gray-500">Erstelle und versende Newsletter an deine Community</p>
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={loadData}
                            disabled={loading}
                            className="p-3 rounded-xl bg-gray-100 hover:bg-gray-200"
                        >
                            <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                        </motion.button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatsCard icon={Users} value={stats.total_subscribers} label="Abonnenten" color="bg-gradient-to-br from-blue-500 to-blue-700" />
                    <StatsCard icon={Send} value={stats.total_newsletters_sent} label="Newsletter versendet" color="bg-gradient-to-br from-green-500 to-emerald-700" />
                    <StatsCard icon={FileText} value={drafts.filter(d => d.status === 'draft').length} label="Entwürfe" color="bg-gradient-to-br from-purple-500 to-purple-700" />
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="flex border-b border-gray-200">
                        {[
                            { id: 'create', icon: Sparkles, label: 'Erstellen' },
                            { id: 'templates', icon: Layout, label: 'Vorlagen', count: templates.length },
                            { id: 'scheduled', icon: CalendarClock, label: 'Geplant', count: scheduled.length },
                            { id: 'abtest', icon: FlaskConical, label: 'A/B-Tests', count: abTests.length },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 font-medium transition-colors ${
                                    activeTab === tab.id 
                                        ? 'text-black border-b-2 border-black bg-gray-50' 
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className="px-2 py-0.5 text-xs bg-gray-200 rounded-full">{tab.count}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    <div className="p-6">
                        {/* CREATE TAB */}
                        {activeTab === 'create' && (
                            <div className="space-y-6">
                                {/* Generate Button */}
                                <motion.button
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                    onClick={generateNewsletter}
                                    disabled={generating}
                                    className="w-full p-6 bg-gradient-to-r from-black to-gray-800 text-white rounded-2xl flex items-center justify-center gap-4 hover:from-gray-900 hover:to-gray-700 disabled:opacity-70"
                                >
                                    {generating ? (
                                        <RefreshCw className="w-6 h-6 animate-spin" />
                                    ) : (
                                        <Sparkles className="w-6 h-6" />
                                    )}
                                    <span className="text-xl font-bold">
                                        {generating ? 'KI generiert Newsletter...' : 'Neuen Newsletter mit KI erstellen'}
                                    </span>
                                </motion.button>

                                {/* Drafts Grid */}
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-bold text-lg">Entwürfe</h3>
                                        {drafts.length > 0 && (
                                            <button
                                                onClick={deleteAllDrafts}
                                                className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                                Alle löschen
                                            </button>
                                        )}
                                    </div>
                                    {drafts.length === 0 ? (
                                        <div className="text-center py-12 bg-gray-50 rounded-xl">
                                            <MailOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                            <p className="text-gray-500">Erstelle deinen ersten Newsletter mit der KI</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {drafts.slice(0, 6).map((draft, idx) => (
                                                <DraftCard key={idx} draft={draft} onLoad={loadDraft} onDelete={deleteDraft} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* TEMPLATES TAB */}
                        {activeTab === 'templates' && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-bold text-lg">Gespeicherte Vorlagen</h3>
                                        <p className="text-sm text-gray-500">Newsletter-Vorlagen für schnelle Wiederverwendung</p>
                                    </div>
                                </div>

                                {templates.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                                        <Layout className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                        <h4 className="font-semibold text-gray-700 mb-2">Keine Vorlagen</h4>
                                        <p className="text-gray-500 text-sm">Erstelle einen Newsletter und speichere ihn als Vorlage</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {templates.map((template, idx) => (
                                            <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow group">
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="p-2 bg-purple-100 rounded-lg">
                                                        <Layout className="w-5 h-5 text-purple-600" />
                                                    </div>
                                                    <button
                                                        onClick={() => deleteTemplate(template.id)}
                                                        className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <h4 className="font-bold text-gray-900 mb-1">{template.name}</h4>
                                                <p className="text-sm text-gray-500 mb-3 truncate">{template.subject}</p>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {
                                                            const parsed = parseNewsletterHTML(template.content);
                                                            setNewsletterData({
                                                                subject: template.subject,
                                                                ...parsed,
                                                                rawHtml: template.content
                                                            });
                                                            setShowEditor(true);
                                                        }}
                                                        className="flex-1 py-2 text-sm font-medium bg-black text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-1"
                                                    >
                                                        <Copy className="w-3 h-3" /> Verwenden
                                                    </button>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-2">
                                                    {new Date(template.created_at).toLocaleDateString('de-DE')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* SCHEDULED TAB */}
                        {activeTab === 'scheduled' && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-bold text-lg">Geplante Newsletter</h3>
                                        <p className="text-sm text-gray-500">Newsletter die zu einem bestimmten Zeitpunkt versendet werden</p>
                                    </div>
                                </div>

                                {scheduled.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                                        <CalendarClock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                        <h4 className="font-semibold text-gray-700 mb-2">Keine geplanten Newsletter</h4>
                                        <p className="text-gray-500 text-sm">Erstelle einen Newsletter und plane den Versand</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {scheduled.map((item, idx) => (
                                            <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
                                                <div className="p-3 bg-amber-100 rounded-lg">
                                                    <Clock className="w-6 h-6 text-amber-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="font-bold text-gray-900">{item.subject}</h4>
                                                    <p className="text-sm text-gray-500">
                                                        Geplant für: {new Date(item.scheduled_for).toLocaleString('de-DE')}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => cancelScheduled(item.id)}
                                                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
                                                >
                                                    Abbrechen
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* A/B TEST TAB */}
                        {activeTab === 'abtest' && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-bold text-lg">A/B-Tests</h3>
                                        <p className="text-sm text-gray-500">Teste verschiedene Betreffzeilen für bessere Öffnungsraten</p>
                                    </div>
                                </div>

                                {abTests.length === 0 ? (
                                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                                        <FlaskConical className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                                        <h4 className="font-semibold text-gray-700 mb-2">Keine A/B-Tests</h4>
                                        <p className="text-gray-500 text-sm">Erstelle einen Newsletter und starte einen A/B-Test</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {abTests.map((test, idx) => (
                                            <div key={idx} className="bg-white border border-gray-200 rounded-xl p-5">
                                                <div className="flex items-start justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-indigo-100 rounded-lg">
                                                            <FlaskConical className="w-5 h-5 text-indigo-600" />
                                                        </div>
                                                        <div>
                                                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                                                test.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                                                test.status === 'running' ? 'bg-blue-100 text-blue-700' :
                                                                'bg-green-100 text-green-700'
                                                            }`}>
                                                                {test.status === 'pending' ? 'Bereit' : test.status === 'running' ? 'Läuft' : 'Abgeschlossen'}
                                                            </span>
                                                            <p className="text-xs text-gray-400 mt-1">
                                                                {new Date(test.created_at).toLocaleDateString('de-DE')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {test.status === 'pending' && (
                                                        <button
                                                            onClick={() => sendABTest(test.id)}
                                                            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800"
                                                        >
                                                            <Play className="w-4 h-4" /> Test starten
                                                        </button>
                                                    )}
                                                </div>
                                                
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="p-3 bg-blue-50 rounded-lg">
                                                        <p className="text-xs text-blue-600 font-medium mb-1">Variante A</p>
                                                        <p className="font-medium text-gray-900">{test.subject_a}</p>
                                                        {test.results?.a && (
                                                            <p className="text-xs text-gray-500 mt-2">
                                                                Gesendet: {test.results.a.sent}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="p-3 bg-purple-50 rounded-lg">
                                                        <p className="text-xs text-purple-600 font-medium mb-1">Variante B</p>
                                                        <p className="font-medium text-gray-900">{test.subject_b}</p>
                                                        {test.results?.b && (
                                                            <p className="text-xs text-gray-500 mt-2">
                                                                Gesendet: {test.results.b.sent}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <p className="text-xs text-gray-400 mt-3">
                                                    Testgruppe: {test.test_percentage}% der Abonnenten
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Subscribers Section */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="p-5 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg font-bold text-gray-900">Abonnenten</h2>
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">{subscribers.length}</span>
                            </div>
                            {subscribers.length > 0 && (
                                <button
                                    onClick={deleteAllSubscribers}
                                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Alle löschen
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 max-h-[300px] overflow-y-auto">
                        {subscribers.length === 0 ? (
                            <div className="col-span-full text-center py-8">
                                <UserPlus className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                                <p className="text-gray-500">Noch keine Abonnenten</p>
                            </div>
                        ) : (
                            subscribers.map((sub, idx) => <SubscriberCard key={idx} subscriber={sub} />)
                        )}
                    </div>
                </div>
            </div>

            {/* Visual Editor Modal */}
            <AnimatePresence>
                {showEditor && newsletterData && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-2xl w-full max-w-4xl my-8 shadow-2xl overflow-hidden"
                        >
                            {/* Editor Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-yellow-50 to-orange-50">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <Wand2 className="w-5 h-5 text-yellow-600" />
                                        Visueller Editor
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-1">Klicke auf Texte zum Bearbeiten • Hover über Bilder für Wechsel-Button</p>
                                </div>
                                <button onClick={() => { setShowEditor(false); setNewsletterData(null); }} className="p-2 hover:bg-white/50 rounded-lg">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Subject Line */}
                            <div className="p-4 bg-gray-50 border-b">
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Betreff</label>
                                <EditableText 
                                    field="subject" 
                                    value={newsletterData.subject} 
                                    className="text-lg font-bold text-gray-900"
                                    placeholder="Newsletter Betreff..."
                                />
                            </div>

                            {/* Visual Newsletter Preview */}
                            <div className="bg-gray-100 p-6">
                                <div className="max-w-[600px] mx-auto bg-white shadow-xl">
                                    {/* Header */}
                                    <div className="p-6 border-b flex items-center justify-between">
                                        <div>
                                            <span className="text-xl font-extrabold tracking-tight">TEAM</span>
                                            <span className="text-xl font-extrabold tracking-tight text-gray-500">WERK</span>
                                        </div>
                                        <span className="text-xs text-gray-400 uppercase tracking-wide">📧 Newsletter</span>
                                    </div>

                                    {/* Hero Image */}
                                    <EditableImage 
                                        field="heroImage" 
                                        src={newsletterData.heroImage} 
                                        category="hero"
                                        className="w-full h-48 object-cover"
                                        alt="Hero"
                                    />

                                    {/* Hero Text */}
                                    <div className="p-6">
                                        <EditableText 
                                            field="headline" 
                                            value={newsletterData.headline} 
                                            className="text-2xl font-extrabold text-black mb-2 leading-tight"
                                            placeholder="Headline eingeben..."
                                        />
                                        <EditableText 
                                            field="subline" 
                                            value={newsletterData.subline} 
                                            className="text-gray-500 mb-4"
                                            placeholder="Subline eingeben..."
                                        />
                                        <EditableText 
                                            field="intro" 
                                            value={newsletterData.intro} 
                                            className="text-gray-700 leading-relaxed"
                                            multiline
                                            placeholder="Intro-Text eingeben..."
                                        />
                                    </div>

                                    {/* Room Section */}
                                    <div className="px-6 pb-6">
                                        <div className="bg-black text-white overflow-hidden rounded-lg">
                                            <EditableImage 
                                                field="roomImage" 
                                                src={newsletterData.roomImage} 
                                                category={newsletterData.featuredRoom}
                                                className="w-full h-40 object-cover"
                                                alt="Raum"
                                            />
                                            <div className="p-5">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <EditableText 
                                                        field="discountReason" 
                                                        value={newsletterData.discountReason} 
                                                        className="text-xs font-bold uppercase tracking-wide text-yellow-400"
                                                        placeholder="Grund..."
                                                    />
                                                    <span className="text-xs text-gray-400">·</span>
                                                    <div className="flex items-center gap-1">
                                                        <EditableText 
                                                            field="discount" 
                                                            value={newsletterData.discount} 
                                                            className="text-xs font-bold text-yellow-400 inline"
                                                            placeholder="15"
                                                        />
                                                        <span className="text-xs font-bold text-yellow-400">% sparen</span>
                                                    </div>
                                                </div>
                                                <div className="text-lg font-bold mb-2">
                                                    {newsletterData.featuredRoom === 'seeblick' ? 'Seeblick – Großer Auftritt' : 'Waldblick – Kreative Sessions'}
                                                </div>
                                                <p className="text-sm text-gray-300 mb-4">
                                                    {newsletterData.featuredRoom === 'seeblick' 
                                                        ? 'Großer Tagungsraum für Konferenzen und Seminare' 
                                                        : 'Moderner Besprechungsraum für kleine Meetings'}
                                                </p>
                                                
                                                {/* Room Toggle */}
                                                <div className="flex gap-2 mb-4">
                                                    <button
                                                        onClick={() => {
                                                            updateField('featuredRoom', 'waldblick');
                                                            updateField('roomImage', '/assets/waldblick/1.jpg');
                                                        }}
                                                        className={`px-3 py-1 text-xs rounded ${newsletterData.featuredRoom === 'waldblick' ? 'bg-white text-black' : 'bg-gray-800 text-gray-400'}`}
                                                    >
                                                        Waldblick
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            updateField('featuredRoom', 'seeblick');
                                                            updateField('roomImage', '/assets/seeblick/1.jpg');
                                                        }}
                                                        className={`px-3 py-1 text-xs rounded ${newsletterData.featuredRoom === 'seeblick' ? 'bg-white text-black' : 'bg-gray-800 text-gray-400'}`}
                                                    >
                                                        Seeblick
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tip Section */}
                                    <div className="px-6 pb-6">
                                        <div className="border-l-4 border-blue-900 pl-4">
                                            <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">💡 Tipp des Monats</div>
                                            <EditableText 
                                                field="tip" 
                                                value={newsletterData.tip} 
                                                className="text-gray-700"
                                                multiline
                                                placeholder="Tipp eingeben..."
                                            />
                                        </div>
                                    </div>

                                    {/* Award Badge */}
                                    <div className="px-6 pb-6">
                                        <div className="bg-gradient-to-r from-yellow-400 to-yellow-300 rounded-xl p-4 text-center">
                                            <img src="/assets/awards/coworking-preis-2025.png" alt="Award" className="h-16 mx-auto mb-2" />
                                            <div className="font-bold text-sm">🏆 1. Platz Deutscher Coworking Preis 2025</div>
                                            <div className="text-xs text-gray-700">Kategorie: Ländlicher/suburbaner Raum</div>
                                        </div>
                                    </div>

                                    {/* Closing Image */}
                                    <EditableImage 
                                        field="closingImage" 
                                        src={newsletterData.closingImage} 
                                        category="closing"
                                        className="w-full h-32 object-cover"
                                        alt="Closing"
                                    />

                                    {/* Footer */}
                                    <div className="bg-black text-white p-6 text-center">
                                        <EditableText 
                                            field="closing" 
                                            value={newsletterData.closing} 
                                            className="text-lg font-semibold mb-3"
                                            placeholder="Abschluss-Text..."
                                        />
                                        <div className="text-xs text-gray-400">
                                            Instagram · LinkedIn · Website
                                        </div>
                                    </div>

                                    {/* Legal Footer */}
                                    <div className="bg-gray-100 p-4 text-center text-xs text-gray-500">
                                        teamWERK Erolzheim · Espachstraße 13 · 88453 Erolzheim
                                        <br />
                                        <span className="text-gray-400">Abmelde-Link wird automatisch eingefügt</span>
                                    </div>
                                </div>
                            </div>

                            {/* AI Refinement Section */}
                            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border-t">
                                <div className="flex items-center gap-2 mb-3">
                                    <Wand2 className="w-4 h-4 text-purple-600" />
                                    <span className="font-semibold text-sm text-gray-700">KI-Assistent</span>
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={refinePrompt}
                                        onChange={(e) => setRefinePrompt(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && refineWithAI()}
                                        className="flex-1 px-4 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="z.B. 'Mach die Headline emotionaler' oder 'Schreibe einen neuen Intro-Text'"
                                    />
                                    <button
                                        onClick={refineWithAI}
                                        disabled={refining}
                                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {refining ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                        Verfeinern
                                    </button>
                                </div>
                            </div>

                            {/* Editor Footer */}
                            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                                <button
                                    onClick={() => { setShowEditor(false); setNewsletterData(null); }}
                                    className="px-6 py-3 text-gray-600 hover:text-gray-900 font-medium"
                                >
                                    Abbrechen
                                </button>
                                
                                <div className="flex items-center gap-3">
                                    {/* Save as Template */}
                                    <button
                                        onClick={() => setShowTemplateDialog(true)}
                                        className="flex items-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100"
                                    >
                                        <Layout className="w-4 h-4" /> Als Vorlage
                                    </button>
                                    
                                    {/* Schedule */}
                                    <button
                                        onClick={() => setShowScheduleDialog(true)}
                                        className="flex items-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100"
                                    >
                                        <CalendarClock className="w-4 h-4" /> Planen
                                    </button>
                                    
                                    {/* A/B Test */}
                                    <button
                                        onClick={() => setShowABTestDialog(true)}
                                        className="flex items-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100"
                                    >
                                        <FlaskConical className="w-4 h-4" /> A/B-Test
                                    </button>
                                    
                                    {/* Send Now */}
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={sendNewsletter}
                                        disabled={sending || !stats.total_subscribers}
                                        className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 shadow-lg"
                                    >
                                        {sending ? (
                                            <><RefreshCw className="w-5 h-5 animate-spin" /> Wird gesendet...</>
                                        ) : (
                                            <><Send className="w-5 h-5" /> Jetzt senden</>
                                        )}
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Image Picker Modal */}
            <AnimatePresence>
                {showImagePicker && (
                    <ImagePickerModal 
                        field={showImagePicker.field}
                        category={showImagePicker.category}
                        onClose={() => setShowImagePicker(null)}
                    />
                )}
            </AnimatePresence>

            {/* Template Save Dialog */}
            <AnimatePresence>
                {showTemplateDialog && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
                        onClick={() => setShowTemplateDialog(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            className="bg-white rounded-xl p-6 max-w-md w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                <Layout className="w-5 h-5" /> Als Vorlage speichern
                            </h3>
                            <input
                                type="text"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                placeholder="Vorlagen-Name (z.B. 'Winter Newsletter')"
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg mb-4 focus:ring-2 focus:ring-black focus:border-transparent"
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setShowTemplateDialog(false)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-900"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={saveAsTemplate}
                                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                                >
                                    Speichern
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Schedule Dialog */}
            <AnimatePresence>
                {showScheduleDialog && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
                        onClick={() => setShowScheduleDialog(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            className="bg-white rounded-xl p-6 max-w-md w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                <CalendarClock className="w-5 h-5" /> Versand planen
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Wählen Sie Datum und Uhrzeit für den automatischen Versand.
                            </p>
                            <input
                                type="datetime-local"
                                value={scheduleDate}
                                onChange={(e) => setScheduleDate(e.target.value)}
                                min={new Date().toISOString().slice(0, 16)}
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg mb-4 focus:ring-2 focus:ring-black focus:border-transparent"
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setShowScheduleDialog(false)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-900"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={scheduleNewsletter}
                                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                                >
                                    Planen
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* A/B Test Dialog */}
            <AnimatePresence>
                {showABTestDialog && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
                        onClick={() => setShowABTestDialog(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            className="bg-white rounded-xl p-6 max-w-lg w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                <FlaskConical className="w-5 h-5" /> A/B-Test erstellen
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Testen Sie zwei verschiedene Betreffzeilen um zu sehen, welche besser funktioniert.
                            </p>
                            
                            <div className="space-y-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Betreff A</label>
                                    <input
                                        type="text"
                                        value={abTestSubjectA}
                                        onChange={(e) => setAbTestSubjectA(e.target.value)}
                                        placeholder={newsletterData?.subject || "Erste Variante..."}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Betreff B</label>
                                    <input
                                        type="text"
                                        value={abTestSubjectB}
                                        onChange={(e) => setAbTestSubjectB(e.target.value)}
                                        placeholder="Zweite Variante..."
                                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Testgruppen-Größe: {abTestPercentage}%</label>
                                    <input
                                        type="range"
                                        min="10"
                                        max="50"
                                        value={abTestPercentage}
                                        onChange={(e) => setAbTestPercentage(parseInt(e.target.value))}
                                        className="w-full"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        {Math.round(stats.total_subscribers * abTestPercentage / 100)} von {stats.total_subscribers} Abonnenten erhalten den Test
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => setShowABTestDialog(false)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-900"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={createABTest}
                                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
                                >
                                    Test erstellen
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NewsletterAdmin;
